import root from 'window-or-global';
import {APP_CONTEXT} from '@/constants';
import {NextURL} from 'next/dist/server/web/next-url';
import {isLocaleSupported} from '@/lib/utils/locale';

const getUrlFromHref = (href: string): URL | null => {
	try {
		const url = new URL(href, 'http://localhost');
		return url;
	} catch {
		return null;
	}
};

/**
 * Ensures a route for the given href without a locale segment.
 * TODO: Is this even needed? Double check relative pathing should never have a locale segment due to middleware.
 */
export const genRoute = (href: string): string => {
	// Leave full URL as is.
	if (href.startsWith('http://') || href.startsWith('https://')) {
		return href;
	}

	// Relative URL processing
	const url = getUrlFromHref(href);
	if (!url) {
		return href; // something went wrong, return the original href
	}

	let segments = url.pathname.split('/').filter(Boolean);

	// Filter out app context segment
	if (segments[0] && segments[0] === APP_CONTEXT.replace(/^\/+/, '')) {
		segments = segments.slice(1);
	}

	// Filter out locale segment (if any)
	if (segments[0] && isLocaleSupported(segments[0])) {
		segments = segments.slice(1);
	}

	return `/${segments.join('/')}${url.search || ''}${url.hash || ''}`;
};

/**
 * router.push/replace interrupts axios responses,
 * We adjust history natively to allow returning from X page
 */
export const safelyReplaceBrowserUrl = (path: string): void => {
	const basePath = process.env.NEXT_PUBLIC_EPIC_SERVER_URL ?? '';
	const {search, hash} = root.location;
	const searchParams = new URLSearchParams(search);
	searchParams.delete('lang'); // remove lang from search params
	const newUrl = new URL(`${process.env.NEXT_PUBLIC_APP_CONTEXT}/${path}`, basePath); // "/redeem" from NEXT_PUBLIC_EPIC_SERVER_URL gets stripped in new URL
	newUrl.search = searchParams.toString();
	newUrl.hash = hash;
	root.history.pushState({}, '', newUrl.toString());
};

// De-duplicate consecutive path segments
// /test/test/123/test/tes1 → /test/123/tes1
export function deduplicatePathSegments(url: NextURL): NextURL {
	const clone = url.clone();
	const segments = clone.pathname.split('/').filter(Boolean);
	const deduped = segments.filter((seg, i) => seg !== segments[i - 1]);
	clone.pathname = '/' + deduped.join('/');
	return clone;
}

// Enforce code parameter in the path.
export function processCodeFromSearchParam(url: NextURL): NextURL {
	const codeParam = url.searchParams.get('code')?.trim();
	if (!codeParam?.length) {
		return url;
	}

	const next = url.clone();
	let parts = next.pathname.split('/').filter(Boolean);
	parts.splice(0, 0, codeParam.toUpperCase());

	next.pathname = `/${parts.join('/')}`;
	next.searchParams.delete('code');
	return next;
}
